import { useEffect, useState } from 'react';
import { AlarmClock, Check } from 'lucide-react';
import { toast } from 'sonner';
import cn from 'classnames';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../../../../redux/features/user/userSelectors';

import { IconButtonTemplate } from '../../buttons/IconButtonTemplate';
import { AddReminderForm } from '../../../form/forms/AddReminderForm';
import { Dialog } from '../../Dialog';
import { ButtonTemplate } from '../../buttons/ButtonTemplate';
import { EditSelect } from '../../EditSelect';
import { DialogConfirm } from '../../DialogConfirm';
import { LoaderProgress } from '../../LoaderProgress';
import { RescheduleReminderForm } from '../../../form/forms/RescheduleReminderForm';
import { ReminderGroupDialog } from '../ReminderGroupDialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../shadcn/popover';

import { showError } from '../../../../utils/ui';
import { editSelectReminders, errorMessages } from '../../../../data/constants';
import {
  useDeleteReminderMutation,
  useFinishReminderMutation,
  useUpdateReminderMutation,
} from '../../../../redux/services/reminders/remindersApi';
import { reminderTypeBadgeClasses, reminderTypeTogleData } from '../../../../lib/mocks/add-reminder-mock';

const urgencyOptionClasses = {
  None: 'text-blackColor hover:bg-gray-200',
  Urgent: 'text-redColor hover:bg-redColor hover:text-whiteColor',
  Important: 'text-orangeColor hover:bg-orangeColor hover:text-whiteColor',
};

export const ItemHeader = ({ reminder, group = [reminder] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editSelect, setEditSelect] = useState(false);

  const [isUrgencyOpen, setIsUrgencyOpen] = useState(false);

  const currentUrgency =
    reminderTypeTogleData.find(
      item => item.title === reminder?.reminder_type?.title
    ) ?? reminderTypeTogleData[0];

  const userToken = useSelector(selectUserToken);
  const [
    deleteReminder,
    { isLoading: isDeletingLoading, error: deletingError },
  ] = useDeleteReminderMutation();
  const [
    finishReminder,
    { isLoading: isFinishingLoading, error: finishingError },
  ] = useFinishReminderMutation();
  const [updateReminder] = useUpdateReminderMutation();

  const handleUrgencyChange = async value => {
    setIsUrgencyOpen(false);
    if (value === currentUrgency.value) return;

    const response = await updateReminder({
      token: userToken,
      id: reminder.id,
      data: {
        reminder_subtype: reminder?.reminder_subtype?.value,
        subject: reminder?.subject,
        date: reminder?.date,
        reminder_type: Number(value),
      },
    });

    if (response?.error) toast.error(errorMessages.wentWrong);
  };

  const handleFinishReminder = async () => {
    setIsLoading(true);
    setError('');

    const responses = await Promise.all(
      group.map(({ id }) => finishReminder({ token: userToken, id }))
    );

    const errorStatus =
      responses.find(response => response?.error)?.error || finishingError;

    setError(errorStatus);
    setIsLoading(false);
  };

  const handleEdit = () => {
    setIsEditOpen(true);
  };

  const handleReschedule = () => {
    setIsRescheduleOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteOpen(true);
  };

  const handleDeleteReminder = async () => {
    setIsLoading(true);
    setError('');

    const response = await deleteReminder({
      token: userToken,
      id: reminder.id,
    });

    const errorStatus = response?.error || deletingError;

    setError(errorStatus);
    setIsLoading(false);
  };

  const addSelectHandlers = () => {
    const newEditSelect = editSelectReminders.map(select => {
      switch (select.title) {
      case 'Edit':
        return { ...select, handleSelection: handleEdit };
      case 'Reschedule':
        return { ...select, handleSelection: handleReschedule };
      case 'Delete':
        return { ...select, handleSelection: handleDelete };
      default:
        return select;
      }
    });

    setEditSelect(newEditSelect);
  };

  useEffect(() => {
    addSelectHandlers();
  }, []);

  useEffect(() => {
    showError(error);
  }, [error]);

  return (
    <>
      {isLoading && <LoaderProgress />}

      <div className={`flex justify-between items-center gap-2 w-full opacity-${reminder?.is_finish ? '60' : '100'}`}>
        <div className="flex gap-2 items-center">
          <span className="text-blackColor text-xs font-bold">
            {reminder.date}
          </span>

          <Popover
            open={isUrgencyOpen}
            onOpenChange={setIsUrgencyOpen}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`w-[76px] py-0.5 text-center text-xs font-bold ${reminderTypeBadgeClasses[currentUrgency.title]} rounded cursor-pointer`}
              >
                {currentUrgency.title}
              </button>
            </PopoverTrigger>

            <PopoverContent
              align="start"
              className="flex flex-col gap-0.5 p-1 w-auto min-w-[110px] bg-whiteColor"
            >
              {reminderTypeTogleData.map(item => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleUrgencyChange(item.value)}
                  className={cn(
                    'px-2 py-1 text-left text-xs font-bold rounded cursor-pointer transition-colors',
                    urgencyOptionClasses[item.title],
                    { 'underline': item.value === currentUrgency.value }
                  )}
                >
                  {item.title}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <IconButtonTemplate
              isSmall={true}
              icon={AlarmClock}
              isRounded={true}
              tooltipText={'Reminders'}
              handleClick={() => setIsGroupOpen(true)}
            />

            <span className="absolute -top-1.5 -right-2 px-1 min-w-[16px] h-4 leading-4 text-[10px] text-center text-whiteColor font-semibold bg-gray-500 rounded pointer-events-none">
              {group.length}
            </span>
          </div>

          <ButtonTemplate
            isIconText={true}
            icon={Check}
            text={reminder?.is_finish ? 'Reopen' : 'Finish'}
            handleClick={handleFinishReminder}
            classes="pl-[0.6rem]"
          />

          <EditSelect selectData={editSelect} />
        </div>
      </div>

      {isGroupOpen && (
        <Dialog
          content={
            <ReminderGroupDialog
              head={reminder}
              group={group}
            />
          }
          isOpen={isGroupOpen}
          onClose={() => setIsGroupOpen(false)}
          classes="max-w-[340px] sm:max-w-[480px]"
        />
      )}

      {isEditOpen && (
        <Dialog
          content={
            <AddReminderForm
              isEdit={true}
              isClientRequest={reminder?.client_request}
              reminder={reminder}
              setIsAddOpen={setIsEditOpen}
            />
          }
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          classes="max-w-[290px] sm:max-w-[420px]"
        />
      )}

      {isRescheduleOpen && (
        <Dialog
          content={
            <RescheduleReminderForm
              reminderId={reminder.id}
              setIsRescheduleOpen={setIsRescheduleOpen}
            />
          }
          isOpen={isRescheduleOpen}
          onClose={() => setIsRescheduleOpen(false)}
        />
      )}

      {isDeleteOpen && (
        <Dialog
          content={
            <DialogConfirm
              handleSubmit={() => handleDeleteReminder()}
              handleCancel={() => setIsDeleteOpen(false)}
            />
          }
          classes={'max-w-[290px] sm:max-w-[420px]'}
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
        />
      )}
    </>
  );
};
